import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { BarChart3 } from "lucide-react";

const money = (n) => `$${(n || 0).toFixed(2)}`;
const day = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const badge = {
  active: "bg-[#81B29A]/20 text-[#2A3B32]",
  "coaching ended": "bg-[#F2EBE5] text-[#5C6A61]",
  refunded: "bg-[#E07A5F]/15 text-[#B5523A]",
  "duplicate auto-refunded": "bg-[#F2CC8F]/40 text-[#2A3B32]",
};

export const SalesOverview = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (open) api.get("/admin/sales").then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [open]);

  const s = data?.summary;
  const tiles = s
    ? [
        { k: "Purchases", v: s.purchases, id: "sales-purchases" },
        { k: "Active coaching", v: s.active_coaching, id: "sales-active" },
        { k: "Refunds", v: `${s.refunds} · ${money(s.refunded_amount)}`, id: "sales-refunds" },
        { k: "Net revenue", v: money(s.net), id: "sales-net" },
      ]
    : [];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid="open-sales-overview"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#2A3B32]/15 text-sm text-[#1C221D] hover:bg-[#2A3B32] hover:text-[#FDFBF7] transition-colors duration-200"
        >
          <BarChart3 size={14} /> Sales
        </button>
      </DialogTrigger>
      <DialogContent data-testid="sales-dialog" className="bg-white rounded-3xl border-[#2A3B32]/10 max-w-3xl max-h-[85vh] overflow-y-auto overflow-x-hidden grid-cols-[minmax(0,1fr)]">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl flex items-center gap-3">
            Discipline Reset sales
            {data?.mode === "test" && (
              <span className="text-xs font-sans px-3 py-1 rounded-full bg-[#F2CC8F]/50 text-[#2A3B32]" data-testid="sales-test-mode">
                Test mode, no real money
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        {!data && <div className="text-[#5C6A61] py-6">Loading…</div>}
        {data?.error && <div className="text-[#E07A5F] py-6">Couldn't load sales right now.</div>}
        {s && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
              {tiles.map((t) => (
                <div key={t.k} className="bg-[#F2EBE5] rounded-2xl p-4">
                  <div className="eyebrow text-[#5C6A61]">{t.k}</div>
                  <div className="font-serif-display text-2xl text-[#1C221D] mt-1" data-testid={t.id}>{t.v}</div>
                </div>
              ))}
            </div>
            <div className="text-xs text-[#5C6A61] mt-2">
              Gross {money(s.gross)} · {s.duplicates_refunded} duplicate payment{s.duplicates_refunded === 1 ? "" : "s"} auto-refunded (not counted in revenue) · before Stripe fees
            </div>
            {data.rows.length === 0 ? (
              <div className="py-8 text-center text-[#5C6A61]" data-testid="sales-empty">No sales yet.</div>
            ) : (
              <ul className="mt-4 divide-y divide-[#2A3B32]/8" data-testid="sales-list">
                {data.rows.map((r, i) => (
                  <li key={i} className="py-3 flex flex-col md:flex-row md:items-center gap-2 md:gap-4" data-testid={`sales-row-${i}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[#1C221D] truncate">{r.client_name}</div>
                      <div className="text-xs text-[#5C6A61] truncate">{r.client_email}</div>
                    </div>
                    <div className="flex items-center gap-3 md:gap-4 shrink-0 text-sm">
                      <div className="text-[#1C221D] w-24">{day(r.date)}</div>
                      <div className="hidden lg:block text-xs text-[#5C6A61] w-36">
                        {r.kind === "purchase" ? `Coaching until ${day(r.coaching_expires_at)}` : r.kind === "refund" ? `Bought ${day(r.purchased_at)}` : "Second payment"}
                      </div>
                      <span className={`text-xs px-3 py-1 rounded-full whitespace-nowrap text-center md:w-44 ${badge[r.status]}`}>{r.status}</span>
                      <div className={`w-16 text-right whitespace-nowrap ml-auto ${r.kind === "purchase" ? "text-[#1C221D]" : "text-[#B5523A]"}`}>
                        {r.kind === "purchase" ? money(r.amount) : `−${money(r.amount)}`}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SalesOverview;
